"use client";

import { useState, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import CartDrawer from "./CartDrawer";
import { setGlobalCartOpener } from "@/lib/cart-utils";
import { usePostHog } from "@/hooks/usePostHog";

export default function CartIcon() {
  const { cart, loading, clearCart } = useCart();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { track } = usePostHog();

  // Register the cart opener function globally
  useEffect(() => {
    setGlobalCartOpener(() => setIsDrawerOpen(true));
  }, []);

  if (loading) {
    return (
      <div className="relative">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        <span className="absolute -top-2 -right-2 bg-[var(--accent)] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          <div className="animate-pulse w-2 h-2 bg-white rounded-full"></div>
        </span>
      </div>
    );
  }

  // Calculate item count from cart lines as backup
  const calculatedCount = cart?.lines.edges.reduce((total, edge) => total + edge.node.quantity, 0) || 0;
  const itemCount = cart?.totalQuantity || calculatedCount;
  
  // Debug cart state with more detail
  console.log('CartIcon - cart state:', {
    totalQuantity: cart?.totalQuantity,
    itemsInLines: cart?.lines.edges.length,
    lineQuantities: cart?.lines.edges.map(edge => edge.node.quantity),
    calculatedCount: calculatedCount,
    finalItemCount: itemCount
  });

  // Auto-clear problematic carts with items but 0 total quantity
  const hasItemsButZeroQuantity = cart?.lines.edges.length > 0 && calculatedCount === 0;
  if (hasItemsButZeroQuantity && !loading) {
    console.log('Auto-clearing cart with legacy 0-quantity items');
    clearCart();
  }

  return (
    <>
      <button 
        onClick={() => {
          // Track cart viewed event
          track('cart_viewed', {
            cart_total: cart ? parseFloat(cart.cost.totalAmount.amount) : 0,
            cart_quantity: itemCount,
            cart_items: cart?.lines.edges.map(edge => ({
              product_id: edge.node.merchandise.product.id,
              product_title: edge.node.merchandise.product.title,
              quantity: edge.node.quantity,
              price: parseFloat(edge.node.merchandise.price.amount)
            })) || [],
            source: 'cart_icon'
          });
          setIsDrawerOpen(true);
        }}
        className="relative hover:opacity-75 transition-opacity"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        {itemCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-[var(--accent)] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-mono">
            {itemCount > 99 ? '99+' : itemCount}
          </span>
        )}
      </button>
      
      <CartDrawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
      />
    </>
  );
}